import React, { useEffect, useRef } from 'react';
import { Flex, RemoteImage, useScreenSize } from '@banhala/ui-kit';
import { bridge } from 'shared/src/utils';
import { useOpenDeeplink } from 'shared/src/hooks';
import type { useRetailAds } from '../../queries/useRetailAds';
import { usePostRetailAdDisplayEvent } from '../../queries/usePostRetailAdDisplayEvent';

interface RetailAdsProps {
  retailAds: ReturnType<typeof useRetailAds>['data'];
}

export const RetailAds = ({ retailAds }: RetailAdsProps) => {
  const { width } = useScreenSize();
  const { openDeeplink } = useOpenDeeplink();
  const previousImageURL = useRef<string | null>(null);
  const { mutate: sendDisplayEvent } = usePostRetailAdDisplayEvent();

  const imageUrl = retailAds?.ad_info?.creative_media_link;
  const targetUrl = retailAds?.ad_info?.creative_link;

  const ratio = retailAds?.ad_info?.creative_media_ratio ?? 7.8125;
  const height = width / ratio;

  useEffect(() => {
    if (!imageUrl) return;
    if (imageUrl === previousImageURL.current) return;

    previousImageURL.current = imageUrl;
    bridge.logAnalyticsEvent({
      name: 'DISPLAY_ADS',
      channel: ['firebase', 'ablylog'],
      analytics: {
        SCREEN: 'SEARCH',
        AD_ID: retailAds?.ad_info?.ad_executable_id,
        AD_PLACEMENT_ID: retailAds?.ad_info?.ad_placement_id,
        ADVERTISING_SUPPLIER: retailAds?.ad_info?.ad_supplier,
        AD_NAME: retailAds?.ad_info?.ad_executable_name,
        CREATIVE_TYPE: retailAds?.ad_info?.creative_type,
      },
    });

    // iOS: SearchAblyAdCell → retailAdClient.sendDisplayEvent
    const executableId = retailAds?.ad_info?.ad_executable_id;
    if (executableId != null) {
      sendDisplayEvent(executableId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

  const handleClick = () => {
    bridge.logAnalyticsEvent({
      name: 'CLICK_ADS',
      channel: ['firebase', 'amplitude', 'facebook', 'ablylog'],
      analytics: {
        SCREEN: 'SEARCH',
        AD_ID: retailAds?.ad_info?.ad_executable_id,
        AD_PLACEMENT_ID: retailAds?.ad_info?.ad_placement_id,
        ADVERTISING_SUPPLIER: retailAds?.ad_info?.ad_supplier,
        AD_NAME: retailAds?.ad_info?.ad_placement_name,
        CREATIVE_TYPE: retailAds?.ad_info?.creative_type,
      },
    });

    if (targetUrl?.startsWith('ably://')) {
      openDeeplink(targetUrl);
    } else if (targetUrl) {
      const deeplink = `ably://webview?url=${targetUrl}`;
      openDeeplink(deeplink);
    }
  };

  if (!imageUrl) return null;

  return (
    <Flex.Row onPress={handleClick}>
      <RemoteImage uri={imageUrl} width={width} height={height} />
    </Flex.Row>
  );
};
